import { forwardRef, InputHTMLAttributes, ReactNode, useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Rendered to the right of the label — the "Forgot?" link on the password. */
  labelAction?: ReactNode;
  error?: string;
  /** Adds the reveal control and starts the field masked. */
  revealable?: boolean;
  /**
   * Keeps the label for assistive tech but takes it off the screen. The layout
   * drops it on the single-field screens, where the placeholder already says
   * what the field is.
   */
  labelHidden?: boolean;
}

/**
 * A labelled input with its error message.
 *
 * The error is wired through aria-describedby and aria-invalid rather than
 * only painted red, so it reaches someone who cannot see the colour.
 */
export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  (
    {
      label,
      labelAction,
      error,
      revealable = false,
      labelHidden = false,
      type = 'text',
      ...inputProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const fieldId = useId();
    const errorId = `${fieldId}-error`;
    const [revealed, setRevealed] = useState(false);

    const resolvedType = revealable ? (revealed ? 'text' : 'password') : type;

    return (
      <div className="flex flex-col gap-1.5">
        <div
          className={
            labelHidden ? 'contents' : 'flex items-baseline justify-between gap-2'
          }
        >
          <label
            htmlFor={fieldId}
            className={
              labelHidden ? 'sr-only' : 'text-xs leading-4 text-auth-label'
            }
          >
            {label}
          </label>
          {labelAction}
        </div>

        <div className="relative">
          <input
            {...inputProps}
            id={fieldId}
            ref={ref}
            type={resolvedType}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={[
              'auth-input w-full rounded-lg border bg-auth-field px-3 py-2.5 text-sm leading-[18px] text-auth-heading',
              'placeholder:text-auth-field-placeholder',
              'focus-visible:outline-hidden focus-visible:ring-2',
              revealable ? 'pr-10' : '',
              // A field in error keeps saying so while it has focus. An accent
              // ring over the red border reads as "fine" on the one field that
              // is not.
              error
                ? 'border-auth-error focus-visible:ring-auth-error'
                : 'border-auth-field-border focus-visible:ring-auth-accent',
            ].join(' ')}
          />

          {revealable ? (
            <button
              type="button"
              onClick={() => setRevealed(current => !current)}
              aria-label={t('app.auth.toggle_password_visibility')}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-auth-field-placeholder hover:text-auth-body"
            >
              {revealed ? (
                <EyeOff aria-hidden="true" className="h-4 w-4" />
              ) : (
                <Eye aria-hidden="true" className="h-4 w-4" />
              )}
            </button>
          ) : null}
        </div>

        {error ? (
          <p id={errorId} className="text-[13px] leading-4 text-auth-error">
            {error}
          </p>
        ) : null}
      </div>
    );
  }
);

AuthField.displayName = 'AuthField';
